// src/pages/company-admin/CompanyProjectsPage.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    PencilSquareIcon,
    TrashIcon,
    ArchiveBoxIcon,
    NoSymbolIcon,
    ChevronDownIcon,
} from '@heroicons/react/24/outline';

import api from 'src/shared/api/axios';
import { listMyProjects, deleteProject, closeProject, archiveProject } from 'src/features/projects/api';
import type { Project } from 'src/features/projects/types';

import AssignProjectPmModal from './AssignProjectPmModal';
import ProjectEditorModal from 'src/features/projects/ProjectEditorModal';

import { useToast } from 'src/shared/ui/toast/useToast';
import { formatProjectDateOnly } from 'src/utils/date';

// ✅ Project 기반 + API가 추가로 붙여주는 표시용 필드 확장
type ProjectRow = Project & {
    pmName?: string | null;
    progressStatus?: string | null;
};

function toDateText(v: any): string {
    if (!v) return '—';
    return formatProjectDateOnly(v, 'Asia/Seoul', '—');
}

function fmtNumber(v: any): string {
    if (v === null || v === undefined || v === '') return '—';
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n)) return String(v);
    return n.toLocaleString('ko-KR');
}

const CompanyProjectsPage: React.FC = () => {
    const toast = useToast();

    const [rows, setRows] = useState<ProjectRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    // 수정 모달
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingProjectId, setEditingProjectId] = useState<string | null>(null);

    // 생성 모달
    const [createModalOpen, setCreateModalOpen] = useState(false);

    // PM 지정 모달
    const [pmModalOpen, setPmModalOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState<{ id: string; name: string } | null>(null);

    const reload = async () => {
        setLoading(true);
        setErr(null);
        try {
            const res = await listMyProjects(); // GET /projects
            const base = (Array.isArray(res.data) ? res.data : []) as ProjectRow[];

            const s = await api.get('/projects/weekly-progress-summary');
            const summaryRows: Array<{
                projectId: string;
                weekOf: string;
                plannedPct: number;
                actualPct: number;
                status: string;
            }> = s.data?.data ?? s.data ?? [];

            const summaryMap = new Map(summaryRows.map((x) => [x.projectId, x]));

            const merged = base.map((p) => {
                const w = summaryMap.get(p.id);
                if (!w) return p;

                return {
                    ...p,
                    plannedProgressCached: w.plannedPct,
                    actualProgressCached: w.actualPct,
                    progressStatusCached: w.status as any,
                    plannedProgress: w.plannedPct,
                    actualProgress: w.actualPct,
                    progressStatus: w.status,
                };
            });

            setRows(merged);
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? '프로젝트 목록을 불러올 수 없습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void reload();
    }, []);

    const handleDelete = async (projectId: string, projectName?: string) => {
        if (!projectName) return;

        const firstOk = window.confirm(
            [
                '⚠️ 프로젝트 완전 삭제',
                '',
                '이 작업은 되돌릴 수 없습니다.',
                '프로젝트 자체가 완전히 삭제되며 복구할 수 없습니다.',
                '',
                '단, WBS가 한 번도 생성되지 않은 프로젝트만 삭제할 수 있습니다.',
                'WBS가 이미 생성된 프로젝트는 삭제되지 않습니다.',
                '',
                `삭제 대상: ${projectName}`,
                '',
                '계속 진행하시겠습니까?',
            ].join('\n'),
        );

        if (!firstOk) return;

        const typed = window.prompt(
            [
                `삭제 대상: ${projectName}`,
                '',
                '정말 삭제하려면 아래 프로젝트 이름을 정확히 입력하세요.',
                '',
                `프로젝트명: ${projectName}`,
            ].join('\n'),
            '',
        );

        if (typed === null) return;

        if (typed.trim() !== projectName.trim()) {
            toast.warning('프로젝트 이름이 정확히 입력되지 않았습니다.', {
                title: '삭제 취소',
                duration: 3500,
            });
            return;
        }

        try {
            await deleteProject(projectId);
            toast.success('프로젝트를 삭제했습니다.', {
                title: '삭제 완료',
            });
            await reload();
        } catch (e: any) {
            const code = e?.response?.data?.error?.code;
            const msg = e?.response?.data?.error?.message ?? '프로젝트 삭제에 실패했습니다.';

            if (code === 'PROJECT_HAS_WBS') {
                toast.warning(msg, {
                    title: '삭제 불가',
                    duration: 4500,
                });
                return;
            }

            setErr(msg);
        }
    };

    const handleClose = async (projectId: string) => {
        if (!window.confirm('이 프로젝트를 "종료(CLOSED)" 상태로 변경하시겠습니까?')) return;

        try {
            await closeProject(projectId);
            await reload();
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? '프로젝트 종료에 실패했습니다.');
        }
    };

    const handleArchive = async (projectId: string) => {
        if (!window.confirm('이 프로젝트를 보관(ARCHIVED)하시겠습니까?')) return;

        try {
            await archiveProject(projectId);
            await reload();
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? '프로젝트 보관에 실패했습니다.');
        }
    };

    // ✅ 표가 커지므로 “상태/우선순위/가시성”은 배지 느낌으로 표시
    const badge = (v: any, kind: 'status' | 'priority' | 'visibility') => {
        const s = (v ?? '—') as string;
        const base = 'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px]';

        if (kind === 'status') {
            if (s === 'ACTIVE') return <span className={`${base} bg-sky-50 text-sky-700 border-sky-100`}>{s}</span>;
            if (s === 'DRAFT') return <span className={`${base} bg-gray-50 text-gray-700 border-gray-100`}>{s}</span>;
            if (s === 'CLOSED')
                return <span className={`${base} bg-amber-50 text-amber-700 border-amber-100`}>{s}</span>;
            if (s === 'ARCHIVED')
                return <span className={`${base} bg-slate-50 text-slate-600 border-slate-100`}>{s}</span>;
            return <span className={`${base} bg-gray-50 text-gray-700 border-gray-100`}>{s}</span>;
        }

        if (kind === 'priority') {
            if (s === 'CRITICAL')
                return <span className={`${base} bg-rose-50 text-rose-700 border-rose-100`}>{s}</span>;
            if (s === 'HIGH') return <span className={`${base} bg-amber-50 text-amber-700 border-amber-100`}>{s}</span>;
            if (s === 'MEDIUM') return <span className={`${base} bg-gray-50 text-gray-700 border-gray-100`}>{s}</span>;
            if (s === 'LOW')
                return <span className={`${base} bg-emerald-50 text-emerald-700 border-emerald-100`}>{s}</span>;
            return <span className={`${base} bg-gray-50 text-gray-700 border-gray-100`}>{s}</span>;
        }

        // visibility
        if (s === 'PRIVATE') return <span className={`${base} bg-gray-50 text-gray-700 border-gray-100`}>{s}</span>;
        if (s === 'INTERNAL') return <span className={`${base} bg-sky-50 text-sky-700 border-sky-100`}>{s}</span>;
        if (s === 'PUBLIC')
            return <span className={`${base} bg-emerald-50 text-emerald-700 border-emerald-100`}>{s}</span>;
        return <span className={`${base} bg-gray-50 text-gray-700 border-gray-100`}>{s}</span>;
    };

    const countText = useMemo(() => `${rows.length.toLocaleString('ko-KR')}개`, [rows.length]);

    const editingProject = editingProjectId ? rows.find((r) => r.id === editingProjectId) ?? undefined : undefined;

    return (
        <div className="p-4 space-y-4">
            {/* 헤더 */}
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <h2 className="text-sm font-semibold text-gray-800">회사 프로젝트</h2>
                    <span className="text-xs text-gray-400">{countText}</span>
                </div>

                <button
                    type="button"
                    className="rounded-lg bg-sky-700 text-white px-3 py-1.5 text-xs hover:bg-sky-800"
                    onClick={() => {
                        setErr(null);
                        setCreateModalOpen(true);
                    }}
                >
                    + 새 프로젝트 생성
                </button>
            </div>

            {/* 에러 메시지 */}
            {err && (
                <div className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">
                    {err}
                </div>
            )}

            {/* 테이블 (컬럼 많으므로 scroll) */}
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <div className="overflow-x-auto">
                    <table className="min-w-[1400px] w-full text-sm">
                        <thead className="bg-gray-50 text-xs text-gray-500">
                            <tr>
                                <th className="px-3 py-3 text-left whitespace-nowrap">Name</th>
                                <th className="px-3 py-2 text-left whitespace-nowrap">Code</th>

                                <th className="px-3 py-2 text-left whitespace-nowrap">Status</th>
                                <th className="px-3 py-2 text-left whitespace-nowrap">Priority</th>
                                <th className="px-3 py-2 text-left whitespace-nowrap">Visibility</th>

                                <th className="px-3 py-2 text-left whitespace-nowrap">PM</th>

                                <th className="px-3 py-2 text-left whitespace-nowrap">Contract Date</th>
                                <th className="px-3 py-2 text-left whitespace-nowrap">Start</th>
                                <th className="px-3 py-2 text-left whitespace-nowrap">End</th>

                                <th className="px-3 py-2 text-right whitespace-nowrap">Contract Price</th>
                                <th className="px-3 py-2 text-left whitespace-nowrap">Currency</th>

                                <th className="px-3 py-2 text-right whitespace-nowrap">Budget</th>
                                <th className="px-3 py-2 text-left whitespace-nowrap">Budget CCY</th>

                                <th className="px-3 py-2 text-left whitespace-nowrap">Incoterms</th>
                                <th className="px-3 py-2 text-left whitespace-nowrap">Dest</th>

                                <th className="px-3 py-2 text-left whitespace-nowrap">Progress %</th>
                                <th className="px-3 py-2 text-left whitespace-nowrap">Status</th>

                                <th className="px-3 py-2 text-left whitespace-nowrap //w-[340px]">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={18} className="px-3 py-6 text-center text-gray-500 text-xs">
                                        프로젝트 목록을 불러오는 중입니다…
                                    </td>
                                </tr>
                            )}

                            {!loading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={18} className="px-3 py-6 text-center text-gray-500 text-xs">
                                        등록된 프로젝트가 없습니다.
                                    </td>
                                </tr>
                            )}

                            {!loading &&
                                rows.map((p) => (
                                    <tr key={p.id} className="border-t hover:bg-gray-50/50 text-xs">
                                        <td className="px-3 py-2 text-sm whitespace-nowrap">
                                            <button
                                                type="button"
                                                className="text-left"
                                                onClick={() => {
                                                    setEditingProjectId(p.id);
                                                    setEditModalOpen(true);
                                                }}
                                            >
                                                <div className="font-medium text-sky-700 hover:underline">{p.name}</div>
                                            </button>

                                            {p.description ? (
                                                <div className="text-[11px] text-gray-400 line-clamp-1 max-w-[360px]">
                                                    {p.description}
                                                </div>
                                            ) : null}
                                        </td>

                                        <td className="px-3 py-2 whitespace-nowrap text-gray-700">{p.code ?? '—'}</td>

                                        <td className="px-3 py-2 whitespace-nowrap">{badge(p.status, 'status')}</td>
                                        <td className="px-3 py-2 whitespace-nowrap">{badge(p.priority, 'priority')}</td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            {badge(p.visibility, 'visibility')}
                                        </td>

                                        <td className="px-3 py-2 whitespace-nowrap text-xs">
                                            <button
                                                type="button"
                                                className="text-sky-700 hover:underline"
                                                onClick={() => {
                                                    setSelectedProject({ id: p.id, name: p.name });
                                                    setPmModalOpen(true);
                                                }}
                                            >
                                                {p.pmName?.trim() ? p.pmName : 'PM 지정'}
                                            </button>
                                        </td>

                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                                            {toDateText((p as any).contractDate)}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                                            {toDateText((p as any).startDate)}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                                            {toDateText((p as any).endDate)}
                                        </td>

                                        <td className="px-3 py-2 whitespace-nowrap text-right text-xs tabular-nums text-gray-800">
                                            {fmtNumber((p as any).contractPrice)}
                                        </td>

                                        {/* ✅ 누락되면 컬럼 틀어짐: 반드시 존재해야 함 */}
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                                            {((p as any).contractCurrency ?? '—').toString()}
                                        </td>

                                        <td className="px-3 py-2 whitespace-nowrap text-right text-xs tabular-nums text-gray-800">
                                            {fmtNumber((p as any).budget)}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                                            {((p as any).budgetCurrency ?? '—').toString()}
                                        </td>

                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                                            {((p as any).incoterms ?? '—').toString()}
                                        </td>

                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                                            {(() => {
                                                const c = ((p as any).destinationCountry ?? '').toString().trim();
                                                const r = ((p as any).destinationRegion ?? '').toString().trim();
                                                if (!c && !r) return '—';
                                                return [c, r].filter(Boolean).join(' / ');
                                            })()}
                                        </td>

                                        <td className="px-3 py-2 whitespace-nowrap text-right text-xs tabular-nums text-gray-800">
                                            {(() => {
                                                const raw =
                                                    (p as any).actualProgress ?? (p as any).actualProgressCached;
                                                const pct = typeof raw === 'number' ? raw : Number(raw ?? 0);
                                                return `${(Number.isFinite(pct) ? pct : 0).toFixed(1)}%`;
                                            })()}
                                        </td>

                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                                            {(
                                                p.progressStatus ??
                                                (p as any).progressStatusCached ??
                                                'NOT_STARTED'
                                            ).toString()}
                                        </td>

                                        <td className="px-3 py-2 whitespace-nowrap text-right">
                                            <div className="inline-flex items-center justify-end gap-2">
                                                <a
                                                    href={`/projects/${p.id}`}
                                                    className="inline-flex items-center rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs text-sky-700 hover:bg-sky-100"
                                                >
                                                    프로젝트로 이동
                                                </a>

                                                {/* TODO: 보관, 종료에 대한 컨셉을 확정하고 구현할 것. */}
                                                {/* {(p.status as any) === 'CLOSED' && (
                                                    <button
                                                        type="button"
                                                        className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                                        onClick={() => handleArchive(p.id)}
                                                        disabled={p.status !== 'CLOSED'}
                                                    >
                                                        <ArchiveBoxIcon className="h-4 w-4" />
                                                        <span>보관</span>
                                                    </button>
                                                )}

                                                {(p.status as any) === 'ACTIVE' && (
                                                    <button
                                                        type="button"
                                                        className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-white px-2 py-1 text-xs text-amber-700 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                                        onClick={() => handleClose(p.id)}
                                                        disabled={p.status !== 'ACTIVE'}
                                                    >
                                                        <NoSymbolIcon className="h-4 w-4" />
                                                        <span>종료</span>
                                                    </button>
                                                )} */}
                                                <button
                                                    type="button"
                                                    className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2 py-1 text-xs text-rose-600 hover:bg-rose-50"
                                                    onClick={() => handleDelete(p.id, p.name)}
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                    <span>삭제</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ✅ 프로젝트 수정 모달 (projectId 기반 fetch/edit) */}
            {editingProjectId && (
                <ProjectEditorModal
                    open={editModalOpen}
                    mode="edit"
                    projectId={editingProjectId}
                    initial={editingProject}
                    onClose={() => {
                        setEditModalOpen(false);
                        setEditingProjectId(null);
                    }}
                    onSaved={async () => {
                        await reload();
                        setEditModalOpen(false);
                        setEditingProjectId(null);
                    }}
                />
            )}

            {/* ✅ 프로젝트 생성 모달 */}
            <ProjectEditorModal
                open={createModalOpen}
                mode="create"
                onClose={() => setCreateModalOpen(false)}
                onSaved={async () => {
                    await reload();
                }}
            />

            {/* ✅ PM 지정 모달 */}
            <AssignProjectPmModal
                open={pmModalOpen}
                project={selectedProject}
                onClose={() => {
                    setPmModalOpen(false);
                    setSelectedProject(null);
                }}
                onAssigned={() => {
                    void reload();
                }}
            />
        </div>
    );
};

export default CompanyProjectsPage;
